import styled, { css, keyframes } from "styled-components";

export const SUPER_BREAKPOINT = "1200px";
export const LARGE_BREAK_POINT_NUMBER = 1040;
export const LARGE_BREAK_POINT = `${LARGE_BREAK_POINT_NUMBER}px`;
export const BREAKPOINT_NUM = 756;
export const BREAKPOINT = `${BREAKPOINT_NUM}px`;
export const BUTTON_SIZE = "40px";
export const SMALL_BUTTON_SIZE = "32px";
export const COLORS = {
  primary: "#E51601",
  altPrimary: "#B91202",
  altSecondary: "#000000",
  badgeGray: `rgba(255, 255, 255, 0.2)`,
  midGray: "#424242",
  secondary: "#FFFFFF",
  secondaryLight: "#A0A0A0",
  accent: "#D4DBDB",
  inputGray: "#222222",
  darkGray: "#1E1E1E",
  black: "#000000",
  background: "#131313",
  tableBackground: "#1D1D1D",
  disabled: "#A9AfAf",
  error: "#E51601",
  warnText: "#E5A501",
  vectorRed: "#E71702",
  vectorGreen: "#26E80E",
  linkBlue: "#0193E5",
  accentRed: "#FF4D38",
  obscure: "rgba(0, 0, 0, .5)",
  alert: "rgba(84, 40, 0, 0.50)",
  warn: "rgba(84, 8, 0, 0.50)",
  highlight: "rgba(229, 165, 1, 1)",
  fastest: "#01B3E580",
  faster: "#E5A50180",
  standard: "#B3E50180",
  yes: "#B3E501",
  no: "rgba(60, 30, 800)",
};

export const SCROLLBAR_STYLES = `
  &::-webkit-scrollbar {
    width: .25rem;
    background-color: transparent;
  }

  &::-webkit-scrollbar-track {
    background-color: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${COLORS.secondaryLight};
    border-radius: 0.25rem;
  }
`;

export const HIDDEN_SCROLLBAR_STYLES = `
   &::-webkit-scrollbar {
    display: none; 
  }
  -ms-overflow-style: none; 
  scrollbar-width: none;
`;

export const RED_GRADIENT = `linear-gradient(
    135deg,
    ${COLORS.background} 57.21%,
    ${COLORS.vectorRed} 100%
  )`;

export const RED_GRADIENT_FIXED = `${RED_GRADIENT} fixed`;

export const CARD_BACKGROUND = `
  background: linear-gradient(
      0deg,
      rgba(0, 0, 0, 0.1) 0%,
      rgba(0, 0, 0, 0.1) 100%
    ),
    ${COLORS.tableBackground};
`;
export const FONTS = {
  body: '"Manrope", sans-serif',
  spaceGrotesk: '"Space Grotesk", sans-serif',
  monospace: "monospace",
};
export const FONT_SIZE = {
  size12: "12px",
  size13: "13px",
  size14: "14px",
  size16: "16px",
  size18: "18px",
  size24: "24px",
  size32: "32px",
  size48: "48px",
  size64: "64px",
  size96: "96px",
};
export const FONT_WEIGHT = {
  weight200: 200,
  weight400: 400,
  weight500: 500,
  weight700: 700,
};

export const Page = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  margin: auto;
  @media print {
    margin: 0;
  }
`;

export const MIN_WIDTH_PAGE_PROPS = `
    display: flex;
    overflow: auto;
    max-width: 100vw;
    ${HIDDEN_SCROLLBAR_STYLES};
`;

const fakeLinkStyles = css<{ disabled?: boolean }>`
  color: ${COLORS.primary};
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  text-decoration: underline;
  align-items: center;
  pointer-events: ${({ disabled }) => (disabled ? "none" : "auto")};
`;

export const FakeLink = styled.div<{ disabled?: boolean }>`
  ${fakeLinkStyles}
`;

export const FakeInlineLink = styled.span<{ disabled?: boolean }>`
  ${fakeLinkStyles}
`;

export const FadingInOutText = styled.span<{ offset: number }>`
  animation: fadeIn 2s infinite;
  animation-delay: ${({ offset }) => offset}s;
  @keyframes fadeIn {
    0% {opacity:1;}
    12% {opacity:0.9;}
    25% {opacity:0.5;}
    38% {opacity:0.9;}
    50% {opacity:1;}
    100% {opacity:1;}
  }
}
`;

export const FlashPCBTable = styled.table<{ width?: string }>`
  width: ${({ width }) => width || "100%"};
  table-layout: fixed;
  word-break: break-word;
  page-break-inside: auto;
`;

export const NoWrapFlashPCBTable = styled(FlashPCBTable)`
  display: inline-block;
  white-space: nowrap;
`;

export const TableHeaderCell = styled.th<{
  width?: string;
  borderBottom?: boolean;
  borderRight?: boolean;
  sticky?: boolean;
  // TODO: remove and replace with align
  alignRight?: boolean;
  textAlign?: string;
}>`
  padding: 0.5rem;
  text-align: left;
  top: 0;
  z-index: 1;
  ${({ sticky }) => sticky && `position: sticky;`}
  width: ${({ width }) => width || "auto"};
  border-right: ${({ borderRight }) =>
    borderRight ? `1px solid ${COLORS.black}` : "none"};
  border-bottom: ${({ borderBottom }) =>
    borderBottom ? `1px solid ${COLORS.black}` : "none"};
  ${({ alignRight }) => alignRight && `text-align: right;`}
  ${({ textAlign }) => textAlign && `text-align: ${textAlign};`}

  ${CARD_BACKGROUND};
`;

const getBackgroundColor = (
  even: boolean,
  alert?: boolean,
  warn?: boolean,
  highlight?: boolean,
  disabled?: boolean,
  go?: boolean,
) => {
  if (disabled) {
    return COLORS.background;
  }
  if (alert) {
    return COLORS.alert;
  }
  if (warn) {
    return COLORS.warn;
  }
  if (highlight) {
    return COLORS.highlight;
  }
  if (go) {
    return COLORS.standard;
  }
  return even ? COLORS.tableBackground : COLORS.inputGray;
};

export const TableRow = styled.tr<{
  alert?: boolean;
  warn?: boolean;
  highlight?: boolean;
  disabled?: boolean;
}>`
  color: ${({ disabled }) => (disabled ? COLORS.disabled : COLORS.secondary)};
  font-weight: ${FONT_WEIGHT.weight400};
  text-decoration: ${({ disabled }) => (disabled ? "line-through" : "default")};
  &:nth-child(even) {
    background: ${({ alert, warn, highlight, disabled }) =>
      getBackgroundColor(true, alert, warn, highlight, disabled)};
  }
  &:nth-child(odd) {
    background: ${({ alert, warn, highlight, disabled }) =>
      getBackgroundColor(false, alert, warn, highlight, disabled)};
  }
`;

export const SelectableTableRow = styled(TableRow)<{ selected?: boolean }>`
  &:nth-child(even) {
    background: ${({ selected }) => (selected ? COLORS.badgeGray : "default")};
  }
  &:nth-child(odd) {
    background: ${({ selected }) => (selected ? COLORS.badgeGray : "default")};
  }

  &:hover {
    background-color: ${({ warn }) => (warn ? "default" : COLORS.badgeGray)};
    cursor: pointer;
  }
`;

export const TableCell = styled.td<{
  borderRight?: boolean;
  // TODO: remove and replace with align
  alignRight?: boolean;
  textAlign?: string;
  verticalPadding?: string;
}>`
  padding: ${({ verticalPadding }) => verticalPadding || "0.125rem"} 0.5rem;

  ${({ borderRight }) =>
    borderRight && `border-right: 1px solid ${COLORS.black}`}
  ${({ alignRight }) => alignRight && `text-align: right;`}
  ${({ textAlign }) => textAlign && `text-align: ${textAlign};`}
`;

export const TableHeaderRow = styled.tr`
  color: ${COLORS.secondaryLight};
  page-break-inside: avoid;
  page-break-before: auto;
  page-break-after: avoid;
  -webkit-print-color-adjust: exact;
  color-adjust: exact;
  font-size: ${FONT_SIZE.size14};

  ${CARD_BACKGROUND};
`;

export const hoverStyles = css`
  &:hover {
    color: ${COLORS.secondary};

    img {
      filter: brightness(150%);
    }
  }
`;

export const CustomPartsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1rem;
`;

export const InventoryContainer = styled.div`
  border: 1px solid ${COLORS.inputGray};
  border-radius: 1.5rem;
  padding: 1rem;
  width: 100%;
`;

export const PriceBreaksFormContainer = styled.div`
  border: 1px solid ${COLORS.inputGray};
  border-radius: 1.5rem;
  padding: 1rem;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

export const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  gap: 1rem;
`;

export const inputStyles = `
    display: flex;
    padding: 0.5rem 1rem;
    border-radius: 0.5rem;
    background: ${COLORS.inputGray};
    color: ${COLORS.secondary};
    font-size: ${FONT_SIZE.size16};
    border: none;
`;

export const PropertyContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  font-size: ${FONT_SIZE.size16};
  font-weight: ${FONT_WEIGHT.weight500};
  color: ${COLORS.secondary};
  overflow-wrap: anywhere;
`;

export const InputLabel = styled.label`
  font-weight: ${FONT_WEIGHT.weight400};
  font-size: ${FONT_SIZE.size13};
  color: ${COLORS.secondaryLight};
`;

export const Input = styled.input`
  ${inputStyles};
  max-width: 100%;
`;

export const TextArea = styled.textarea<{ minHeight?: string }>`
  ${inputStyles};
  min-height: ${({ minHeight }) =>
    minHeight !== undefined ? minHeight : "initial"};
  width: 100%;
`;

export const InfoPageContainer = styled.div`
  margin: auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  font-weight: ${FONT_WEIGHT.weight500};
  font-family: ${FONTS.spaceGrotesk};
`;

const shimmerAnimation = keyframes`
  0% {
    background-position: 100% 50%;
  }
  100% {
    background-position: -100% 50%;
  }
`;

export const Pill = styled.div<{ width?: string }>`
  height: 6px;
  border-radius: 100px;
  background: linear-gradient(
    270deg,
    rgba(255, 255, 255, 0.05) 0%,
    rgba(255, 255, 255, 0.1) 61.48%,
    rgba(255, 255, 255, 0.2) 84%,
    rgba(255, 255, 255, 0.05) 99.26%
  );
  background-size: 200% 100%;
  animation: ${shimmerAnimation} 4s linear infinite;

  ${({ width }) => width && `width: ${width};`};
`;

export const paragraphStyles = `
  color: ${COLORS.secondaryLight};
  font-size: ${FONT_SIZE.size18};
  font-weight: ${FONT_WEIGHT.weight400};
  line-height: 1.5rem;
  margin: 1rem 0;

  a {
    color: ${COLORS.secondary};
    font-weight: ${FONT_WEIGHT.weight500};
  }
`;

export const SIDEBAR_WIDTH = "212px";

export const VerticalLine = styled.div<{ height?: string }>`
  border-left: 1.5px solid ${COLORS.primary};
  height: 100%;

  ${({ height }) => height && `height: ${height};`};
`;

export const PageTitle = styled.div`
  font-family: ${FONTS.spaceGrotesk};
  font-size: ${FONT_SIZE.size64};
  font-weight: ${FONT_WEIGHT.weight700};

  @media (max-width: ${BREAKPOINT}) {
    font-size: ${FONT_SIZE.size32};
  }
`;

export const HelpfulLinks = styled.div`
  display: flex;
  gap: 1rem;
`;

export const formLabelStyles = `
  > label {
    font-size: ${FONT_SIZE.size13};
    color: ${COLORS.secondaryLight};
    font-weight: ${FONT_WEIGHT.weight400};

    > span {
      filter: brightness(50%);
    }
  }
`;

export const formInputStyles = `
  > input,
  textarea {
    display: flex;
    padding: 0.5rem 1rem;
    border-radius: 0.5rem;
    background: ${COLORS.inputGray};
    color: ${COLORS.secondary};
    border: none;
    line-height: 1.5rem;

    // overriding the autofill styles
    &:-webkit-autofill {
      -webkit-box-shadow: 0 0 0 50px ${COLORS.inputGray} inset;
      -webkit-text-fill-color: ${COLORS.secondary};
    }
  }

  > textarea {
    min-height: 2rem;

    ${SCROLLBAR_STYLES};
  }

  > input::placeholder,
  > textarea::placeholder {
    color: ${COLORS.secondaryLight};
  }
`;

export const ShowIfAboveWidth = styled.div<{ width: string }>`
  display: inherit;
  @media (max-width: ${({ width }) => width}) {
    display: none;
  }
`;

export const HideIfAboveWidth = styled.div<{ width: string }>`
  display: none;
  @media (max-width: ${({ width }) => width}) {
    display: inherit;
  }
`;

export const ToolTitle = styled.div`
  text-align: center;
  font-size: ${FONT_SIZE.size24};
  font-family: ${FONTS.spaceGrotesk};
`;

export const ToolCardContainer = styled.div`
  border-radius: 1rem;
  ${CARD_BACKGROUND};
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

export const FormulaLine = styled.div`
  background-color: ${COLORS.tableBackground};
  font-family: ${FONTS.monospace};
  font-size: ${FONT_SIZE.size13};
  color: ${COLORS.secondaryLight};
  padding: 1rem;
`;
